// Drawing for freehand strokes, connectors, images and frames.
import type { Connector, Frame, Freehand, Image } from '../../../shared/types.ts';
import type { DrawContext } from './index.ts';
import { footerRect } from '../../model/columns.ts';
import { strokeCurve, strokeFrame, strokePoint } from '../freehand.ts';
import { arrowPoints, CONNECTOR_WIDTH } from '../connectors.ts';
import type { ImageCache } from '../images.ts';
import { FONT_FAMILY } from '../../text/layout.ts';

export const FRAME_TITLE_PX = 13;      // screen px
export const FRAME_TITLE_GAP_PX = 6;   // screen px between title and frame
const FRAME_BORDER = 'rgba(0, 0, 0, 0.14)';
const FRAME_TITLE = '#5f5f5f';
const COLUMN_BG = '#F1F2F4';
const COLUMN_RADIUS = 12;
const COLUMN_COUNT = '#9AA0A6';
const COLUMN_ADD = '#5F6368';
const COLUMN_ADD_PX = 14;               // world px: it scales with the board
const PLACEHOLDER = '#E9EAEE';
const PLACEHOLDER_ICON = '#C3C6CF';

// --- freehand (spec §5.5: Catmull-Rom through the points, as cubic Béziers) ---

interface StrokePaths { key: string; smooth: Path2D; coarse: Path2D }
const strokeCache = new WeakMap<readonly number[], StrokePaths>();

function strokePaths(o: Freehand): StrokePaths {
  const f = strokeFrame(o);
  const key = `${f.kx},${f.ky},${f.pad}`;
  const hit = strokeCache.get(o.points);
  if (hit && hit.key === key) return hit;
  const n = o.points.length >> 1;
  const p = (i: number) => strokePoint(f, o.points, Math.max(0, Math.min(n - 1, i)));

  const smooth = new Path2D();
  const curve = strokeCurve(o);
  const [x0, y0] = curve.start;
  smooth.moveTo(x0, y0);
  if (n === 1) smooth.lineTo(x0 + 0.01, y0);
  for (const c of curve.curves) smooth.bezierCurveTo(...c);

  // Level of detail: a polyline through a sample of the points.
  const coarse = new Path2D();
  const step = Math.max(1, Math.floor(n / 24));
  coarse.moveTo(x0, y0);
  for (let i = step; i < n; i += step) coarse.lineTo(...p(i));
  coarse.lineTo(...p(n - 1));

  const paths = { key, smooth, coarse };
  strokeCache.set(o.points, paths);
  return paths;
}

export function drawFreehand(ctx: CanvasRenderingContext2D, o: Freehand, lod: boolean): void {
  const paths = strokePaths(o);
  ctx.strokeStyle = o.stroke;
  ctx.lineWidth = o.strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke(lod ? paths.coarse : paths.smooth);
}

// --- connectors (drawn in world coords) ---

export function drawConnector(ctx: CanvasRenderingContext2D, c: Connector, pts: readonly [number, number][], lod: boolean, scale: number): void {
  if (pts.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(pts[0]![0], pts[0]![1]);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]![0], pts[i]![1]);
  ctx.strokeStyle = c.stroke;
  ctx.lineWidth = Math.max(CONNECTOR_WIDTH, 1 / scale);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.stroke();
  if (lod) return; // spec §5.1: no arrowheads at low zoom
  ctx.fillStyle = c.stroke;
  if (c.arrowEnd) arrowHead(ctx, pts[pts.length - 2]!, pts[pts.length - 1]!);
  if (c.arrowStart) arrowHead(ctx, pts[1]!, pts[0]!);
}

function arrowHead(ctx: CanvasRenderingContext2D, from: readonly [number, number], tip: readonly [number, number]): void {
  const [a, b, c] = arrowPoints(from, tip);
  ctx.beginPath();
  ctx.moveTo(a![0], a![1]);
  ctx.lineTo(b![0], b![1]);
  ctx.lineTo(c![0], c![1]);
  ctx.closePath();
  ctx.fill();
}

// --- images ---

export function drawImageObject(ctx: CanvasRenderingContext2D, o: Image, images: ImageCache, scale: number): void {
  const bitmap = images.get(o.assetHash, Math.max(o.w, o.h) * scale);
  if (bitmap) {
    ctx.drawImage(bitmap, 0, 0, o.w, o.h);
    return;
  }
  // Placeholder until the bitmap arrives: a card with a small landscape glyph.
  ctx.fillStyle = PLACEHOLDER;
  ctx.fillRect(0, 0, o.w, o.h);
  const s = Math.min(o.w, o.h) * 0.25;
  const cx = o.w / 2, cy = o.h / 2;
  ctx.fillStyle = PLACEHOLDER_ICON;
  ctx.beginPath();
  ctx.moveTo(cx - s, cy + s * 0.6);
  ctx.lineTo(cx - s * 0.3, cy - s * 0.3);
  ctx.lineTo(cx + s * 0.1, cy + s * 0.2);
  ctx.lineTo(cx + s * 0.45, cy - s * 0.1);
  ctx.lineTo(cx + s, cy + s * 0.6);
  ctx.closePath();
  ctx.fill();
}

// --- frames ---

export function drawFrame(ctx: CanvasRenderingContext2D, f: Frame, dc: DrawContext): void {
  const { zoom, scale } = dc;
  const column = f.layout === 'column';
  if (column) {
    // A Kanban column (spec §13.6): a grey lane the white cards sit in.
    ctx.fillStyle = COLUMN_BG;
    ctx.beginPath();
    ctx.roundRect(0, 0, f.w, f.h, COLUMN_RADIUS);
    ctx.fill();
    if (dc.editable && !dc.lod) {
      ctx.font = `500 ${COLUMN_ADD_PX}px ${FONT_FAMILY}`;
      ctx.fillStyle = COLUMN_ADD;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      const r = footerRect(f);
      ctx.fillText('+ Add card', r.x - f.x + 8, r.y - f.y + r.h / 2);
    }
  } else {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, f.w, f.h);
    ctx.strokeStyle = FRAME_BORDER;
    ctx.lineWidth = 1 / scale;
    ctx.strokeRect(0, 0, f.w, f.h);
  }

  // Title above the frame at a constant screen size; a column adds its card count.
  const size = FRAME_TITLE_PX / zoom;
  ctx.font = `${column ? 600 : 500} ${size}px ${FONT_FAMILY}`;
  ctx.fillStyle = FRAME_TITLE;
  ctx.textBaseline = 'bottom';
  ctx.textAlign = 'left';
  const y = -FRAME_TITLE_GAP_PX / zoom;
  const count = column ? ` · ${f.children.length}` : '';
  const title = ellipsize(ctx, f.title || (column ? 'Column' : 'Frame'), f.w - ctx.measureText(count).width);
  ctx.fillText(title, 0, y);
  if (count) {
    const titleW = ctx.measureText(title).width;
    ctx.font = `500 ${size}px ${FONT_FAMILY}`;
    ctx.fillStyle = COLUMN_COUNT;
    ctx.fillText(count, titleW, y);
  }
}

function ellipsize(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(`${text.slice(0, mid)}…`).width <= max) lo = mid;
    else hi = mid - 1;
  }
  return lo ? `${text.slice(0, lo)}…` : '';
}
